/**
 * Sign up — both web portals (design approved 26 Sep 2026).
 *
 * Three short steps (`useEmailFirstSignUp`, @tonnie/sdk):
 *
 *   1. email (or LinkedIn / Google / Microsoft / Apple) — an address that
 *      already has an account is said here, with the way to sign in
 *   2. first name, last name, a password (the rule + strength meter)
 *   3. the 6-digit code sent to the address → signed in → the portal's
 *      `afterSignUp`
 *
 * `heading` replaces the first step's title and line — Business says
 * "Join {company}" to someone arriving from a team invitation.
 *
 * An invitation (`?invite=1&email=`) names its — masked — address in a
 * callout and does not pre-fill it (see AcceptInvitePage: a different address
 * is recoverable there). A plain `?email=` (from "no account with this email"
 * on /login) fills step 1.
 *
 * Observability (Part 22): PAGE_VIEWED; AUTH_SIGNUP_SUBMITTED at step 1;
 * AUTH_SIGNUP_VERIFICATION_SENT / _RESENT / _COMPLETED; AUTH_SIGNUP_SUCCEEDED;
 * AUTH_SIGNUP_FAILED with Clerk's code.
 */

import { useAuth } from '@clerk/clerk-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { AlertCircle, Loader2, Mail } from '@tonnie/assets';
import { classifyClerkError } from '@tonnie/foundation';
import {
  useAuthErrorMessage,
  usePasswordRevealLabels,
  usePasswordStrengthLabels,
  useTranslation,
} from '@tonnie/locale';
import { useAnalytics, useErrorCapture, usePageView } from '@tonnie/observability';
import {
  emailStepSchema,
  meetsPasswordRule,
  signUpDetailsSchema,
  type EmailStepInput,
  type SignUpDetailsInput,
} from '@tonnie/schemas';
import { useEmailFirstSignUp, type SignUpOutcome } from '@tonnie/sdk';
import {
  Alert,
  AlertDescription,
  AuthEmailChip,
  Button,
  Input,
  Label,
  PasswordInput,
  PasswordStrength,
} from '@tonnie/ui';
import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useLocation } from 'wouter';

import { goToDestination, useAccountPortal } from '../portal';
import { AccountAuthLayout } from './AccountAuthLayout';
import { AlreadySignedInCard } from './AlreadySignedInCard';
import { CodeStep } from './CodeStep';
import { SocialSignInSection } from './SocialSignInSection';

export interface SignUpViewProps {
  /** The first step's title and line, in place of the default ones. */
  heading?: { title: string; description: string };
}

export function SignUpView({ heading }: SignUpViewProps) {
  const { t } = useTranslation();
  const [, setLocation] = useLocation();
  const { afterSignUp, legal } = useAccountPortal();
  const { isLoaded: clerkLoaded, isSignedIn } = useAuth();
  const flow = useEmailFirstSignUp();
  const describeAuthError = useAuthErrorMessage();
  const { track } = useAnalytics();
  const { captureException } = useErrorCapture();
  usePageView('signup');

  const [error, setError] = useState<ReactNode>(null);
  const [codeError, setCodeError] = useState<string | null>(null);

  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const invitationEmail = params.get('invite') === '1' ? params.get('email') : null;
  const prefill = invitationEmail ? '' : (params.get('email') ?? '');

  const settle = useCallback(
    (result: SignUpOutcome, fallback: string, show: (message: string) => void): void => {
      switch (result.outcome) {
        case 'next':
          setError(null);
          setCodeError(null);
          return;
        case 'complete':
          track('AUTH_SIGNUP_VERIFICATION_COMPLETED', {});
          track('AUTH_SIGNUP_SUCCEEDED', { method: 'email' });
          goToDestination(afterSignUp(), setLocation);
          return;
        case 'failed': {
          const failure = describeAuthError(result.error, fallback);
          track('AUTH_SIGNUP_FAILED', { method: 'email', error_code: failure.code });
          if (!failure.expected) {
            captureException(
              result.error instanceof Error ? result.error : new Error(String(result.error)),
              { page: 'signup', step: flow.state.step }
            );
          }
          show(failure.message);
          return;
        }
        default: {
          const exhaustive: never = result;
          throw new Error(`Unhandled sign-up outcome: ${JSON.stringify(exhaustive)}`);
        }
      }
    },
    [describeAuthError, track, captureException, setLocation, afterSignUp, flow.state.step]
  );

  if (clerkLoaded && isSignedIn) {
    return <AlreadySignedInCard brand="sign-up" />;
  }

  const { state } = flow;

  switch (state.step) {
    case 'email':
      return (
        <AccountAuthLayout
          brand="sign-up"
          title={heading?.title ?? t('auth.flow.signUp.title')}
          description={heading?.description ?? t('auth.flow.signUp.description')}
        >
          {invitationEmail && (
            <Alert data-testid="auth-signup-invitation-callout">
              <Mail className="h-4 w-4" aria-hidden="true" />
              <AlertDescription>
                {t('auth.flow.invitation.signUp', { email: invitationEmail })}
              </AlertDescription>
            </Alert>
          )}
          <SocialSignInSection mode="sign-up" />
          <SignUpEmailStep
            defaultEmail={prefill}
            error={error}
            ready={flow.ready}
            onSubmit={async (email) => {
              setError(null);
              track('AUTH_SIGNUP_SUBMITTED', { method: 'email' });
              const result = await flow.start(email);
              if (
                result.outcome === 'failed' &&
                classifyClerkError(result.error).kind === 'email_taken'
              ) {
                track('AUTH_SIGNUP_FAILED', { method: 'email', error_code: 'email_taken' });
                setError(
                  <>
                    {t('auth.flow.signUp.emailTaken')}{' '}
                    <Link
                      href={`/login?email=${encodeURIComponent(email)}`}
                      className="font-semibold underline"
                    >
                      {t('auth.flow.signUp.signInInstead')}
                    </Link>
                  </>
                );
                return;
              }
              settle(result, t('auth.errors.unableToCreate'), setError);
            }}
          />
          <p className="text-center text-sm text-muted-foreground">
            {t('auth.flow.signUp.haveAccount')}{' '}
            <Link
              href="/login"
              className="font-semibold text-primary-accessible hover:underline"
              data-testid="auth-signup-login-link"
            >
              {t('auth.flow.signUp.signIn')}
            </Link>
          </p>
          <p className="text-center text-xs leading-relaxed text-muted-foreground">
            {t('auth.flow.signUp.legal')}{' '}
            <LegalLink href={legal.terms}>{t('auth.flow.signUp.terms')}</LegalLink>
            {' · '}
            <LegalLink href={legal.privacy}>{t('auth.flow.signUp.privacy')}</LegalLink>
          </p>
        </AccountAuthLayout>
      );

    case 'details':
      return (
        <AccountAuthLayout
          brand="sign-up"
          eyebrow={
            <p className="text-sm font-semibold text-primary-accessible">
              {t('auth.flow.signUp.step', { current: 2, total: 3 })}
            </p>
          }
          title={t('auth.flow.signUp.detailsTitle')}
          description={
            <AuthEmailChip
              email={state.email}
              changeLabel={t('auth.flow.change')}
              onChange={flow.back}
            />
          }
          onBack={flow.back}
        >
          <DetailsStep
            email={state.email}
            error={error}
            onSubmit={async (details) => {
              setError(null);
              const result = await flow.submitDetails(details);
              if (result.outcome === 'next') track('AUTH_SIGNUP_VERIFICATION_SENT', {});
              settle(result, t('auth.errors.unableToCreate'), setError);
            }}
          />
          <p className="text-center text-sm text-muted-foreground">
            {t('auth.flow.signUp.codeNext')}
          </p>
        </AccountAuthLayout>
      );

    case 'code':
      return (
        <AccountAuthLayout
          brand="sign-up"
          eyebrow={
            <p className="text-sm font-semibold text-primary-accessible">
              {t('auth.flow.signUp.step', { current: 3, total: 3 })}
            </p>
          }
          title={t('auth.flow.signUp.codeTitle')}
          description={t('auth.flow.code.description', { email: state.email })}
          onBack={flow.back}
        >
          <CodeStep
            label={t('auth.flow.code.label')}
            submitLabel={t('auth.flow.signUp.codeSubmit')}
            error={codeError}
            testIdPrefix="auth-signup"
            onSubmit={async (code) =>
              settle(
                await flow.verify(code),
                t('auth.errors.invalidVerificationCode'),
                setCodeError
              )
            }
            onResend={async () => {
              const result = await flow.resendCode();
              if (result.outcome === 'next') track('AUTH_SIGNUP_VERIFICATION_RESENT', {});
              settle(result, t('auth.errors.unableToResend'), setCodeError);
              return result.outcome === 'next';
            }}
          />
        </AccountAuthLayout>
      );

    default: {
      const exhaustive: never = state;
      throw new Error(`Unhandled sign-up step: ${JSON.stringify(exhaustive)}`);
    }
  }
}

/** An in-app page through the router; another site in a new tab. */
function LegalLink({ href, children }: { href: string; children: ReactNode }) {
  const className = 'font-medium text-primary-accessible hover:underline';
  if (href.startsWith('/') && !href.startsWith('//')) {
    return (
      <Link href={href} className={className}>
        {children}
      </Link>
    );
  }
  return (
    <a href={href} target="_blank" rel="noreferrer" className={className}>
      {children}
    </a>
  );
}

function FormError({ message }: { message: ReactNode }) {
  if (!message) return null;
  return (
    <Alert variant="destructive" data-testid="auth-signup-error">
      <AlertCircle className="h-4 w-4" aria-hidden="true" />
      <AlertDescription>{message}</AlertDescription>
    </Alert>
  );
}

function SignUpEmailStep({
  defaultEmail,
  error,
  ready,
  onSubmit,
}: {
  defaultEmail: string;
  error: ReactNode;
  ready: boolean;
  onSubmit: (email: string) => Promise<void>;
}) {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<EmailStepInput>({
    resolver: zodResolver(emailStepSchema),
    defaultValues: { email: defaultEmail },
  });

  return (
    <form
      onSubmit={handleSubmit(({ email }) => onSubmit(email))}
      noValidate
      className="flex flex-col gap-4"
      data-testid="auth-signup-form"
    >
      <FormError message={error} />
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="signup-email" className="text-sm font-medium">
          {t('auth.flow.emailLabel')}
        </Label>
        <Input
          id="signup-email"
          type="email"
          autoComplete="email"
          placeholder={t('auth.flow.emailPlaceholder')}
          className="h-11 text-base"
          aria-invalid={!!errors.email}
          data-testid="auth-signup-email"
          {...register('email')}
        />
        {errors.email?.message && (
          <p className="text-sm text-destructive-accessible">{t(errors.email.message)}</p>
        )}
      </div>
      {/* Mount point for Clerk's bot protection: without it Clerk falls back
          to an invisible challenge that hangs automated and flagged browsers. */}
      <div id="clerk-captcha" data-cl-theme="auto" data-cl-size="flexible" />
      <Button
        type="submit"
        className="h-11 text-base font-semibold"
        disabled={isSubmitting || !ready}
        data-testid="auth-signup-continue"
      >
        {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
        {t('auth.flow.continue')}
      </Button>
    </form>
  );
}

function DetailsStep({
  email,
  error,
  onSubmit,
}: {
  email: string;
  error: ReactNode;
  onSubmit: (details: SignUpDetailsInput) => Promise<void>;
}) {
  const { t } = useTranslation();
  const reveal = usePasswordRevealLabels();
  const strengthLabels = usePasswordStrengthLabels();
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<SignUpDetailsInput>({
    resolver: zodResolver(signUpDetailsSchema),
    defaultValues: { firstName: '', lastName: '', password: '' },
  });
  const [firstName, lastName, password] = watch(['firstName', 'lastName', 'password']);

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="flex flex-col gap-4">
      <FormError message={error} />
      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="signup-first-name" className="text-sm font-medium">
            {t('auth.flow.signUp.firstName')}
          </Label>
          <Input
            id="signup-first-name"
            autoComplete="given-name"
            autoFocus
            className="h-11 text-base"
            aria-invalid={!!errors.firstName}
            data-testid="auth-signup-first-name"
            {...register('firstName')}
          />
          {errors.firstName?.message && (
            <p className="text-sm text-destructive-accessible">{t(errors.firstName.message)}</p>
          )}
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="signup-last-name" className="text-sm font-medium">
            {t('auth.flow.signUp.lastName')}
          </Label>
          <Input
            id="signup-last-name"
            autoComplete="family-name"
            className="h-11 text-base"
            aria-invalid={!!errors.lastName}
            data-testid="auth-signup-last-name"
            {...register('lastName')}
          />
          {errors.lastName?.message && (
            <p className="text-sm text-destructive-accessible">{t(errors.lastName.message)}</p>
          )}
        </div>
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="signup-password" className="text-sm font-medium">
          {t('auth.flow.signUp.passwordLabel')}
        </Label>
        <PasswordInput
          id="signup-password"
          revealLabels={reveal}
          autoComplete="new-password"
          className="h-11 text-base"
          aria-invalid={!!errors.password}
          aria-describedby="signup-password-strength"
          data-testid="auth-signup-password"
          {...register('password')}
        />
        {errors.password?.message && (
          <p className="text-sm text-destructive-accessible">{t(errors.password.message)}</p>
        )}
        <PasswordStrength
          id="signup-password-strength"
          className="mt-1"
          password={password}
          meetsRule={meetsPasswordRule(password)}
          userInputs={[firstName, lastName, email]}
          labels={strengthLabels}
        />
      </div>
      <Button
        type="submit"
        className="h-11 text-base font-semibold"
        disabled={isSubmitting}
        data-testid="auth-signup-submit"
      >
        {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
        {t('auth.flow.signUp.submit')}
      </Button>
    </form>
  );
}
